import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Clock, Target, CheckCircle2, Star, Award, Shield, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import CTAButtons from '../components/CTAButtons';

const AboutPage = () => {
  const values = [
    {
      icon: Shield,
      title: 'Trust & Security',
      description: 'We prioritize the security of your intellectual property with industry-leading practices and confidential handling of all your trademark information.'
    },
    {
      icon: Users,
      title: 'Client-Focused',
      description: 'Our dedicated team works closely with you, providing personalized attention and tailored solutions for your unique trademark needs.'
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'We maintain the highest standards of service quality, ensuring accurate filings and comprehensive brand protection strategies.'
    },
    {
      icon: Target,
      title: 'Results-Driven',
      description: 'Our proven track record speaks for itself with thousands of successful trademark registrations and satisfied clients nationwide.'
    }
  ];

  const statistics = [
    { number: '15K+', label: 'Trademarks Registered' },
    { number: '98%', label: 'Success Rate' },
    { number: '10+', label: 'Years of Experience' },
    { number: '24/7', label: 'Support Available' }
  ];

  const industries = [
    'Technology & Software',
    'E-commerce & Retail',
    'Food & Beverage',
    'Fashion & Apparel',
    'Healthcare & Wellness',
    'Professional Services',
    'Entertainment & Media',
    'Manufacturing',
    'Real Estate',
    'Education',
    'Hospitality',
    'Financial Services'
  ];

  return (
    <div>
      <Helmet>
        <title>About Us - Global Trademark Pro | Expert Trademark Registration Team</title>
        <meta name="description" content="Learn about Global Trademark Pro - your trusted partner in brand protection. Over 15K trademarks registered with a 98% success rate. Expert trademark attorneys dedicated to protecting your brand." />
        <meta name="keywords" content="about trademark pro, trademark attorneys, brand protection experts, USPTO trademark services, trademark registration company" />
        <link rel="canonical" href="https://yourdomain.com/about" />
      </Helmet>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-emerald-50 via-white to-blue-50 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-4 px-4 py-2 bg-emerald-100 text-emerald-800 rounded-full text-xl font-semibold">
              About Us
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Your Trusted Partner in Brand Protection
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              At Global Trademark Pro, we're passionate about helping businesses protect their most valuable asset—their brand identity. With years of experience and a commitment to excellence, we've become a trusted name in trademark registration services.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Story</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Founded in 2015, Global Trademark Pro emerged from a simple observation: businesses needed a more accessible, affordable, and reliable way to protect their trademarks. Our founders, experienced trademark attorneys, recognized that the traditional process was often complicated and expensive.
                </p>
                <p>
                  We set out to change that. By combining legal expertise with modern technology and a client-first approach, we've helped thousands of businesses secure their brand identity. From startups to established companies, we've been there every step of the way.
                </p>
                <p>
                  Today, we're proud to be one of the leading trademark registration services in the United States, known for our transparent pricing, exceptional service, and high success rate. Our mission remains the same: to make trademark protection accessible to every business, regardless of size or budget.
                </p>
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1758518731462-d091b0b4ed0d"
                alt="Our team"
                className="rounded-2xl shadow-2xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-emerald-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              These principles guide everything we do and every decision we make
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-blue-500 rounded-2xl flex items-center justify-center mb-6">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Why Choose Global Trademark Pro?
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Fast & Efficient</h3>
                  <p className="text-gray-600">We process applications quickly without compromising accuracy, getting your trademark filed promptly.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Target className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Expert Guidance</h3>
                  <p className="text-gray-600">Our experienced attorneys provide personalized advice tailored to your specific business needs.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">High Success Rate</h3>
                  <p className="text-gray-600">Our thorough preparation and expertise result in one of the highest approval rates in the industry.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Star className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Transparent Pricing</h3>
                  <p className="text-gray-600">No hidden fees, no surprises. You'll know exactly what you're paying for from the start.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-24 bg-gradient-to-br from-blue-50 to-emerald-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-12 text-center">
              Our Approach to Brand Protection
            </h2>
            <div className="grid md:grid-cols-2 gap-12">
              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Comprehensive Analysis</h3>
                  <p className="text-gray-700 leading-relaxed">
                    We begin every engagement with a thorough analysis of your trademark needs. Our team examines your brand, industry, and competitive landscape to develop a customized strategy that maximizes your protection while minimizing costs.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Expert Preparation</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Our experienced attorneys meticulously prepare your trademark application, ensuring every detail is accurate and compliant with USPTO requirements. We handle the complex legal work so you don't have to worry about technicalities.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Proactive Communication</h3>
                  <p className="text-gray-700 leading-relaxed">
                    We keep you informed at every stage of the process. You'll receive regular updates on your application status, and our team is always available to answer your questions and address any concerns promptly.
                  </p>
                </div>
              </div>
              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Strategic Guidance</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Beyond just filing applications, we provide strategic advice on building and maintaining a strong trademark portfolio. Our guidance helps you make informed decisions about expanding your brand protection as your business grows.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Ongoing Support</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Our relationship doesn't end when your trademark is registered. We offer continued support for renewals, monitoring, and enforcement to ensure your brand remains protected for years to come.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Affordable Excellence</h3>
                  <p className="text-gray-700 leading-relaxed">
                    We believe quality trademark services should be accessible to all businesses. That's why we've structured our pricing to be transparent and affordable without compromising on the quality of service you receive.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Sets Us Apart */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              What Sets Us Apart
            </h2>
            <div className="space-y-8">
              <div className="bg-gradient-to-br from-emerald-50 to-blue-50 rounded-2xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Industry Expertise</h3>
                <p className="text-gray-700 leading-relaxed">
                  With over a decade of experience in trademark law, our team has handled thousands of applications across virtually every industry imaginable. We understand the nuances of trademark law and stay current with the latest USPTO guidelines and procedures. This deep expertise translates into better outcomes for our clients.
                </p>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-emerald-50 rounded-2xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Technology-Driven Efficiency</h3>
                <p className="text-gray-700 leading-relaxed">
                  We leverage cutting-edge technology to streamline the trademark registration process. Our proprietary systems enable faster processing, better communication, and more accurate results. While we use technology to enhance efficiency, every application receives careful human review by our experienced attorneys.
                </p>
              </div>
              <div className="bg-gradient-to-br from-emerald-50 to-blue-50 rounded-2xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Personalized Service</h3>
                <p className="text-gray-700 leading-relaxed">
                  Unlike large, impersonal law firms, we treat every client as a priority. You'll work with dedicated professionals who understand your business and are invested in your success. We take the time to explain the process, answer your questions, and provide guidance tailored to your specific situation.
                </p>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-emerald-50 rounded-2xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Proven Track Record</h3>
                <p className="text-gray-700 leading-relaxed">
                  Our success rate speaks for itself. We've helped thousands of businesses successfully register and protect their trademarks. Our clients range from startups and small businesses to established brands, all benefiting from our comprehensive approach to trademark protection.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Commitment */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-emerald-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">
              Our Commitment to You
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              At Global Trademark Pro, we're committed to providing exceptional trademark services that give you peace of mind. We understand that your brand is one of your most valuable assets, and we treat it with the care and attention it deserves.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              Our team is dedicated to making the trademark registration process as smooth and stress-free as possible. From your initial consultation to the final registration certificate, we're with you every step of the way, ensuring your brand receives the protection it needs to thrive.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Whether you're protecting your first trademark or managing a complex portfolio, you can count on Global Trademark Pro for expert guidance, transparent pricing, and results you can trust.
            </p>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Success in Numbers
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Delivering exceptional results that speak for themselves
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {statistics.map((stat, index) => (
              <div key={index} className="text-center p-8 bg-gradient-to-br from-emerald-50 to-blue-50 rounded-2xl shadow-lg">
                <div className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-blue-600 mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-700 font-semibold text-lg">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-24 bg-gradient-to-br from-blue-50 to-emerald-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Industries We Serve
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Our expertise spans across diverse industries, helping businesses of all types protect their brand identities with tailored trademark solutions
              </p>
            </div>
            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
              {industries.map((industry, index) => (
                <div 
                  key={index} 
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 text-center"
                >
                  <p className="text-gray-800 font-semibold">{industry}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12">
              <div className="bg-gradient-to-br from-emerald-500 to-blue-500 rounded-3xl p-12 text-white shadow-2xl">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                  <Target className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-3xl font-bold mb-6">Our Mission</h2>
                <p className="text-lg leading-relaxed text-white/95 mb-4">
                  To empower businesses of all sizes by making professional trademark registration services accessible, affordable, and straightforward.
                </p>
                <p className="text-lg leading-relaxed text-white/95">
                  We strive to demystify the trademark process and provide every client with the expert guidance they need to protect their brand confidently and efficiently.
                </p>
              </div>
              <div className="bg-gradient-to-br from-blue-500 to-emerald-500 rounded-3xl p-12 text-white shadow-2xl">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                  <Award className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-3xl font-bold mb-6">Our Vision</h2>
                <p className="text-lg leading-relaxed text-white/95 mb-4">
                  To become the most trusted and innovative trademark service provider in the United States, recognized for our commitment to client success and brand protection excellence.
                </p>
                <p className="text-lg leading-relaxed text-white/95">
                  We envision a future where every business, from startups to enterprises, has the tools and support needed to build and protect powerful brands.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Brand Protection Matters */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-emerald-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Why Brand Protection Matters
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Building Brand Equity</h3>
                <p className="text-gray-700 leading-relaxed">
                  Your trademark is more than just a name or logo—it's the foundation of your brand identity. A registered trademark helps you build brand equity over time, creating a valuable asset that can appreciate as your business grows. It distinguishes your products and services from competitors and builds customer trust and loyalty.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Legal Protection & Exclusive Rights</h3>
                <p className="text-gray-700 leading-relaxed">
                  A registered trademark gives you exclusive rights to use your mark nationwide in connection with your goods or services. It provides legal protection against infringement and allows you to take action if someone tries to copy or misuse your brand. This protection is essential in today's competitive marketplace where brand confusion can damage your reputation and bottom line.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Business Growth & Expansion</h3>
                <p className="text-gray-700 leading-relaxed">
                  As your business expands into new markets or product lines, your trademark protection grows with you. A strong trademark portfolio makes your business more attractive to investors and partners, and it can become a significant asset in mergers, acquisitions, or licensing opportunities. Protecting your trademark early sets the foundation for long-term business success.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Consumer Confidence & Recognition</h3>
                <p className="text-gray-700 leading-relaxed">
                  Consumers rely on trademarks to identify products and services they trust. A registered trademark with the ® symbol signals credibility and professionalism, giving customers confidence in choosing your brand. It helps establish your presence in the marketplace and builds the recognition needed for sustained business growth and customer loyalty.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-blue-900 via-emerald-900 to-blue-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Protect Your Brand?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Join thousands of satisfied clients who trust Global Trademark Pro with their trademark registration needs.
          </p>
          <CTAButtons className="justify-center" variant="dark" />
        </div>
      </section>
    </div>
  );
};

export default AboutPage;